import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, X } from 'lucide-react';

interface AISuggestionLoadingBarProps {
  onDiscard: () => void;
  disabled?: boolean;
  state?: 'generating' | 'awaiting';
  aiMode?: 'auto' | 'suggestions' | 'manual';
}

export function AISuggestionLoadingBar({
  onDiscard,
  disabled = false,
  state = 'generating',
  aiMode = 'manual'
}: AISuggestionLoadingBarProps) {
  const { t } = useTranslation('conversations');
  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="p-3">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                {t('aiSuggestion.title')}
              </span>
              <div className="flex items-center gap-1">
                {state === 'generating' ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b border-primary" />
                    <span className="text-xs text-muted-foreground">{t('aiSuggestion.generating')}</span>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">{t('aiSuggestion.awaitingResponse')}</span>
                )}
              </div>
            </div>
            
            {aiMode !== 'suggestions' && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDiscard}
                  disabled={disabled}
                  className="h-7 w-7 p-0 hover:bg-destructive/20 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>

          {/* Loading Content */}
          <div className="bg-background rounded-md p-3 border">
            <div className="space-y-2">
              <div className="h-4 bg-muted animate-pulse rounded w-3/4"></div>
              <div className="h-4 bg-muted animate-pulse rounded w-1/2"></div>
              <div className="h-4 bg-muted animate-pulse rounded w-2/3"></div>
            </div>
          </div>

          {/* Loading Action Buttons */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={true}
              className="flex-1 gap-2 opacity-50"
            >
              <Sparkles className="h-3 w-3" />
              {state === 'generating' ? t('aiSuggestion.generatingSuggestion') : t('aiSuggestion.awaitingCustomer')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}